// @core/stores/billing/BillingInvoiceNumberingStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { billingApi } from '@core/api';
import type {
  CreateInvoiceNumberingSchemeRequest,
  InvoiceNumberingScheme,
  UpdateInvoiceNumberingSchemeRequest,
} from '@core/types/billing';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class BillingInvoiceNumberingStore {
  // List state
  items: InvoiceNumberingScheme[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: InvoiceNumberingScheme | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, InvoiceNumberingScheme>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): InvoiceNumberingScheme | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: InvoiceNumberingScheme | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create invoice numbering scheme
   */
  async CreateInvoiceNumberingScheme(data: CreateInvoiceNumberingSchemeRequest): Promise<InvoiceNumberingScheme> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await billingApi.CreateInvoiceNumberingScheme(data);
      const item = response.scheme;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete invoice numbering scheme
   */
  async DeleteInvoiceNumberingScheme(scheme_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await billingApi.DeleteInvoiceNumberingScheme(scheme_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== scheme_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === scheme_id) {
          this.currentItem = null;
        }

        this.removeFromCache(scheme_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get invoice numbering scheme
   */
  async GetInvoiceNumberingScheme(scheme_id: string): Promise<InvoiceNumberingScheme> {
    // Check cache first
    const cached = this.cache.get(scheme_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await billingApi.GetInvoiceNumberingScheme(scheme_id);
      const item = response.scheme;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List invoice numbering schemes
   */
  async ListInvoiceNumberingSchemes(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await billingApi.ListInvoiceNumberingSchemes(params);

      runInAction(() => {
        this.items = response.schemes?.schemes ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update invoice numbering scheme
   */
  async UpdateInvoiceNumberingScheme(scheme_id: string, data: UpdateInvoiceNumberingSchemeRequest): Promise<InvoiceNumberingScheme> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await billingApi.UpdateInvoiceNumberingScheme(scheme_id, data);
      const item = response.scheme;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const billingInvoiceNumberingStore = new BillingInvoiceNumberingStore();
